const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const getRowMetric = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value !== undefined && value !== null && value !== '') {
            const numeric = Number(value);
            if (Number.isFinite(numeric)) return numeric;
        }
    }
    return 0;
};

const getStatsMetric = (stats, keys) => {
    for (const key of keys) {
        const metric = stats?.[key];
        if (metric && typeof metric === 'object' && metric.count != null) {
            const numeric = Number(metric.count);
            if (Number.isFinite(numeric)) return numeric;
        }
        if (metric !== undefined && metric !== null && metric !== '') {
            const numeric = Number(metric);
            if (Number.isFinite(numeric)) return numeric;
        }
    }
    return 0;
};

const normalizePercentMetric = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    if (numeric > 0 && numeric <= 1) return numeric * 100;
    return numeric;
};

export const getCompletionConfig = (channelKey) => {
    if (channelKey === 'audio') {
        return {
            countLabel: 'Completed Audio Plays',
            rateLabel: 'Audio Completion Rate',
            countKeys: [
                'VCOMP_95',
                'vcomp_95',
                'VCOMP_75',
                'vcomp_75',
                'COMPLETED_AUDIO_PLAYS',
                'completed_audio_plays',
                'AUDIO_COMPLETED_PLAYS',
                'audio_completed_plays',
                'AUDIO_PLAYS_COMPLETED',
                'audio_plays_completed',
                'COMPLETED_PLAYS',
                'completed_plays'
            ],
            rateKeys: [
                'VCOMP_RATE',
                'vcomp_rate',
                'VIEW_PERCENT',
                'view_percent',
                'AUDIO_COMPLETION_RATE',
                'audio_completion_rate',
                'AUDIO_PLAYTHROUGH_RATE',
                'audio_playthrough_rate',
                'PLAYTHROUGH_RATE',
                'playthrough_rate',
                'COMPLETION_RATE',
                'completion_rate'
            ]
        };
    }

    if (channelKey === 'video' || channelKey === 'ctv') {
        return {
            countLabel: 'Video Completed',
            rateLabel: 'Video Completion Rate',
            countKeys: [
                'VCOMP_95',
                'vcomp_95',
                'VCOMP_75',
                'vcomp_75',
                'VIDEO_COMPLETED',
                'video_completed',
                'COMPLETED_VIDEO',
                'completed_video',
                'VIDEO_COMPLETES',
                'video_completes',
                'VIDEO_COMPLETED_VIEWS',
                'video_completed_views',
                'COMPLETED_VIEWS',
                'completed_views'
            ],
            rateKeys: [
                'VCOMP_RATE',
                'vcomp_rate',
                'VIEW_PERCENT',
                'view_percent',
                'VIDEO_COMPLETION_RATE',
                'video_completion_rate',
                'VIDEO_PLAYTHROUGH_RATE',
                'video_playthrough_rate',
                'PLAYTHROUGH_RATE',
                'playthrough_rate',
                'COMPLETION_RATE',
                'completion_rate'
            ]
        };
    }

    return null;
};

export const getProgrammaticCompletionMetrics = (row, channelKey) => {
    const config = getCompletionConfig(channelKey);
    if (!config) {
        return {
            completedCount: 0,
            completionRate: 0
        };
    }

    return {
        completedCount: getRowMetric(row, config.countKeys),
        completionRate: normalizePercentMetric(getRowMetric(row, config.rateKeys))
    };
};

const sumCompletionRows = (rows, channelKey) => {
    const list = Array.isArray(rows) ? rows : [];
    const totals = list.reduce((acc, row) => {
        const completion = getProgrammaticCompletionMetrics(row, channelKey);
        const impressions = getRowMetric(row, ['IMPRESSIONS', 'impressions']);
        acc.completedCount += completion.completedCount;
        acc.impressions += impressions;
        if (completion.completionRate > 0) {
            const weight = impressions > 0 ? impressions : 1;
            acc.weightedRate += completion.completionRate * weight;
            acc.rateWeight += weight;
        }
        return acc;
    }, {
        completedCount: 0,
        impressions: 0,
        weightedRate: 0,
        rateWeight: 0
    });

    return {
        completedCount: totals.completedCount,
        completionRate: totals.rateWeight > 0 ? (totals.weightedRate / totals.rateWeight) : 0
    };
};

const buildMetric = (count) => ({ count: safeNum(count) });

export const buildChannelCompletionBundle = ({
    channel,
    previousChannel,
    campaignRows,
    previousCampaignRows,
    channelKey
}) => {
    const config = getCompletionConfig(channelKey);
    if (!config) {
        return {
            config: null,
            currentCompleted: buildMetric(0),
            previousCompleted: buildMetric(0),
            currentRate: buildMetric(0),
            previousRate: buildMetric(0)
        };
    }

    const currentRowTotals = sumCompletionRows(campaignRows, channelKey);
    const previousRowTotals = sumCompletionRows(previousCampaignRows, channelKey);

    const currentCompleted = getStatsMetric(channel?.stats, config.countKeys) || currentRowTotals.completedCount;
    const previousCompleted = getStatsMetric(previousChannel?.stats, config.countKeys) || previousRowTotals.completedCount;
    const currentRate = normalizePercentMetric(getStatsMetric(channel?.stats, config.rateKeys)) || currentRowTotals.completionRate;
    const previousRate = normalizePercentMetric(getStatsMetric(previousChannel?.stats, config.rateKeys)) || previousRowTotals.completionRate;

    return {
        config,
        currentCompleted: buildMetric(currentCompleted),
        previousCompleted: buildMetric(previousCompleted),
        currentRate: buildMetric(currentRate),
        previousRate: buildMetric(previousRate)
    };
};
